/**
 * Utility functions for restaurant URL slug generation and parsing
 */

/**
 * Generates a URL-safe slug from text
 * Handles special characters, accented characters, and edge cases
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')                     // Decompose accented characters
    .replace(/[\u0300-\u036f]/g, '')      // Remove diacritics
    .replace(/[^a-z0-9\s-]/g, '')         // Remove special characters
    .replace(/\s+/g, '-')                 // Replace spaces with hyphens
    .replace(/-+/g, '-')                  // Collapse multiple hyphens
    .replace(/^-|-$/g, '')                // Trim leading/trailing hyphens
    .substring(0, 50);                    // Limit length for URL friendliness
}

/**
 * Generates SEO-friendly restoId slug from restaurant name and ID
 * Format: slugified-name-numericId
 * Example: "Frituur Alpha" + "4917505" -> "frituur-alpha-4917505"
 */
export function generateRestoSlug(name: string, id: string | number): string {
  const slug = slugify(name);
  const numericId = String(id);

  // If slugification results in empty string, just return the ID
  if (!slug) {
    return numericId;
  }

  return `${slug}-${numericId}`;
}

/**
 * Extracts numeric restaurant ID from a slug or plain numeric ID
 * Handles both formats for backward compatibility:
 *   - "frituur-alpha-4917505" -> "4917505"
 *   - "4917505" -> "4917505"
 */
export function extractRestoId(slugOrId: string): string {
  // If it's already a pure numeric ID, return as-is
  if (/^\d+$/.test(slugOrId)) {
    return slugOrId;
  }

  // Extract the numeric ID from the end of the slug
  // Work backwards to find the last segment that is purely numeric
  const segments = slugOrId.split('-');

  for (let i = segments.length - 1; i >= 0; i--) {
    if (/^\d+$/.test(segments[i])) {
      return segments[i];
    }
  }

  // Fallback: return the original (will likely fail API call, but maintains behavior)
  return slugOrId;
}

/**
 * Language-specific path segment for chain pages
 *   nl -> /keten/{slug}, fr -> /chaine/{slug}, en -> /chain/{slug}
 */
export function getChainPath(lang: string): string {
  if (lang === 'nl') return 'keten';
  if (lang === 'fr') return 'chaine';
  return 'chain';
}

/** Language-specific path segment for the chain rankings page */
export function getChainRankingsPath(lang: string): string {
  if (lang === 'nl') return 'ketens';
  if (lang === 'fr') return 'chaines';
  return 'chains';
}
